// PackageConsumeSection.jsx (no-grey)
import React from 'react';
import Icon from '@mui/material/Icon';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { Section } from './Section';

const bg = (tokens, fallback = '#fff') => tokens?.surface || fallback;
const br = (tokens, fallback = 'rgba(0,0,0,0.10)') => tokens?.border || fallback;

export function PackageConsumeSection({
  sx,
  tokens,
  consumeType,
  setConsumeType,
  note,
  setNote,
  activityOptions = [],
  activityKey,
  setActivityKey,
  activityHint,
}) {
  const theme = useMuiTheme();
  const isDark = !!tokens?.isDark;

  const SURFACE = bg(tokens, isDark ? '#0b1220' : '#fff'); // ✅ alpha yok
  const BORDER = br(tokens, isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)');

  const pillSx = (active, tone) => {
    const main = theme.palette[tone].main;
    return {
      borderRadius: '16px',
      textTransform: 'none',
      fontWeight: 900,
      height: 42,
      minHeight: 42,
      justifyContent: 'space-between',
      px: 1.2,
      ...(active
        ? {
            // ✅ background MDButton gradient, ekstra “premium glow”
            boxShadow: `0 10px 26px ${alpha(main, isDark ? 0.24 : 0.18)}`,
            '&:hover': { transform: 'translateY(-1px)' },
          }
        : {
            backgroundColor: 'transparent',
            borderWidth: '1.5px',
            '&:hover': {
              backgroundColor: 'transparent', // ✅ gri yok
              borderColor: alpha(main, isDark ? 0.55 : 0.45),
              boxShadow: `0 10px 26px ${alpha(main, isDark ? 0.1 : 0.08)}`,
            },
          }),
    };
  };

  return (
    <Section title="Seans Ayarı" icon="tune" sx={sx}>
      <MDBox sx={{ display: 'grid', gap: 1.05 }}>
        {/* ✅ Segmented container: background = surface (gri değil) */}
        <MDBox
          sx={{
            p: 1,
            borderRadius: '20px',
            border: `1px solid ${BORDER}`,
            background: SURFACE, // ✅ düz
          }}
        >
          <MDTypography variant="caption" sx={{ opacity: 0.78, fontWeight: 900, mb: 0.7 }}>
            Seans türü
          </MDTypography>

          <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.9 }}>
            <MDButton
              variant={consumeType === 'NORMAL' ? 'gradient' : 'outlined'}
              color={consumeType === 'NORMAL' ? 'info' : 'dark'}
              onClick={() => setConsumeType?.('NORMAL')}
              startIcon={<Icon sx={{ fontSize: 18 }}>redeem</Icon>}
              sx={pillSx(consumeType === 'NORMAL', 'info')}
            >
              <span>Paket</span>
              {consumeType === 'NORMAL' ? (
                <Icon sx={{ fontSize: 18 }}>check_circle</Icon>
              ) : (
                <span />
              )}
            </MDButton>

            <MDButton
              variant={consumeType === 'TELAFI' ? 'gradient' : 'outlined'}
              color={consumeType === 'TELAFI' ? 'warning' : 'dark'}
              onClick={() => setConsumeType?.('TELAFI')}
              startIcon={<Icon sx={{ fontSize: 18 }}>history</Icon>}
              sx={pillSx(consumeType === 'TELAFI', 'warning')}
            >
              <span>Telafi</span>
              {consumeType === 'TELAFI' ? (
                <Icon sx={{ fontSize: 18 }}>check_circle</Icon>
              ) : (
                <span />
              )}
            </MDButton>
          </MDBox>
        </MDBox>

        {/* ✅ Aktivite: background = surface */}
        <MDBox
          sx={{
            p: 1,
            borderRadius: '20px',
            border: `1px solid ${BORDER}`,
            background: SURFACE, // ✅ düz
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="caption" sx={{ opacity: 0.78, fontWeight: 900 }}>
                Aktivite
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {activityKey
                  ? activityOptions.find((x) => x.key === activityKey)?.label || activityKey
                  : 'Seçiniz'}
              </MDTypography>
            </MDBox>
            <Icon sx={{ opacity: 0.7 }}>fitness_center</Icon>
          </MDBox>

          {(activityOptions?.length || 0) > 0 ? (
            <MDBox sx={{ mt: 0.9, display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {activityOptions.map((a) => {
                const on = a.key === activityKey;
                return (
                  <MDButton
                    key={a.key}
                    variant={on ? 'gradient' : 'outlined'}
                    color={on ? 'info' : 'dark'}
                    size="small"
                    onClick={() => setActivityKey?.(a.key)}
                    sx={{
                      borderRadius: '999px',
                      textTransform: 'none',
                      fontWeight: 900,
                      px: 1.2,
                      minHeight: 34,
                      ...(on
                        ? {
                            boxShadow: `0 10px 22px ${alpha(
                              theme.palette.info.main,
                              isDark ? 0.22 : 0.16
                            )}`,
                          }
                        : { backgroundColor: 'transparent' }),
                    }}
                  >
                    {a.label}
                  </MDButton>
                );
              })}
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ mt: 0.9, display: 'block', opacity: 0.75 }}>
              Ortak aktivite bulunamadı.
            </MDTypography>
          )}

          {activityHint ? (
            <MDTypography variant="caption" sx={{ mt: 0.7, display: 'block', opacity: 0.8 }}>
              {activityHint}
            </MDTypography>
          ) : null}
        </MDBox>

        {/* ✅ Not: background = surface, gri yok */}
        <MDInput
          label="Not (opsiyonel)"
          value={note}
          onChange={(e) => setNote?.(e.target.value)}
          fullWidth
          sx={{
            ...tokens?.input,
            '& .MuiOutlinedInput-root': {
              borderRadius: '18px',
              background: SURFACE, // ✅ düz
            },
          }}
          InputProps={{
            startAdornment: (
              <MDBox sx={{ mr: 1, opacity: 0.7, display: 'flex', alignItems: 'center' }}>
                <Icon fontSize="small">notes</Icon>
              </MDBox>
            ),
          }}
        />
      </MDBox>
    </Section>
  );
}
